<template>
  <div :class="['rd-anchor', rootClass]" @keydown="onKeydown">
    <slot name="trigger" :open="open" :toggle="toggle" :close="close" :attrs="triggerAttrs" :on="triggerOn"></slot>
    <div v-if="open" ref="panel" :class="['rd-pop', panelClass]" role="dialog" tabindex="-1" :aria-label="label || null">
      <slot :close="close"></slot>
    </div>
  </div>
</template>

<script>
/**
 * Dugme sa iskačućim izborom (period, firme, kolone, magacin...): otvara se klikom na dugme iz slota `trigger`, zatvara klikom van,
 * Esc-om ili ponovnim klikom, a fokus ulazi u izbor pri otvaranju i vraća se na dugme pri zatvaranju.
 * Slot `trigger` dobija { attrs, on, open, toggle, close }: `<button v-bind="attrs" v-on="on">` daje aria-haspopup, aria-expanded i klik.
 * Slot sa sadržajem dobija { close }; `close(true)` vraća fokus na dugme ("Gotovo", izbor jedne stavke).
 * `label` je naziv izbora za čitač ekrana; `panel-class` je varijanta izbora (rd-pop--companies...), `root-class` klasa uz sidro.
 * `focus` je selektor elementa u izboru koji prima fokus pri otvaranju (zadano sam izbor). Događaji: `open`, `close`.
 */
export default {
  name: "UiPopover",
  props: {
    label: { type: String, default: "" },
    panelClass: { type: [String, Array, Object], default: null },
    rootClass: { type: [String, Array, Object], default: null },
    focus: { type: String, default: "" },
  },
  data: () => ({
    open: false,
  }),
  computed: {
    triggerAttrs() {
      return { "aria-haspopup": "dialog", "aria-expanded": String(this.open) };
    },
    triggerOn() {
      return { click: this.toggle };
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onOutside);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onOutside);
  },
  methods: {
    toggle(event) {
      if (this.open) {
        this.close(true);
        return;
      }
      this.trigger = (event && event.currentTarget) || null;
      this.$emit("open");
      this.open = true;
      this.$nextTick(() => {
        this.keepInViewport();
        const panel = this.$refs.panel;
        const target = panel && this.focus ? panel.querySelector(this.focus) : null;
        (target || panel)?.focus();
      });
    },
    /** Izbor koji bi izašao van desne ivice ekrana (telefon, dugme daleko od lijeve ivice) pomjera se ulijevo taman toliko da stane. */
    keepInViewport() {
      const panel = this.$refs.panel;
      if (!panel) {
        return;
      }
      const margin = 8;
      panel.style.left = "";
      const overflow = panel.getBoundingClientRect().right - (document.documentElement.clientWidth - margin);
      if (overflow > 0) {
        panel.style.left = `${-overflow}px`;
      }
      const left = panel.getBoundingClientRect().left;
      if (left < margin) {
        panel.style.left = `${(parseFloat(panel.style.left) || 0) + (margin - left)}px`;
      }
    },
    close(restoreFocus = false) {
      if (!this.open) {
        return;
      }
      this.open = false;
      this.$emit("close");
      if (restoreFocus) {
        const trigger = this.trigger && document.contains(this.trigger) ? this.trigger : this.$el.querySelector("button");
        this.$nextTick(() => trigger?.focus());
      }
    },
    onOutside(event) {
      if (this.open && !this.$el.contains(event.target)) {
        this.close();
      }
    },
    onKeydown(event) {
      if (event.key === "Escape" && this.open) {
        event.stopPropagation();
        this.close(true);
      }
    },
  },
};
</script>
